// useNotificationSocket.js
import { useEffect, useRef } from "react";

/**
 * Ouvre (et maintient) la connexion WebSocket vers /ws/notifications/.
 * `onMessage` reçoit chaque notification au format renvoyé par
 * notification_service.notify_user() côté backend.
 *
 * Reconnexion automatique avec backoff (1s → 2s → 4s ... plafonné à 30s) si
 * la connexion tombe — réseau instable, redéploiement backend, etc.
 * Ne se connecte pas du tout si l'utilisateur n'est pas authentifié.
 */
export function useNotificationSocket(user, onMessage) {
  const wsRef = useRef(null);
  const retryDelay = useRef(1000);
  const closedByUs = useRef(false);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage; // évite de recréer le socket à chaque re-render du parent

  useEffect(() => {
    if (!user) return undefined;

    closedByUs.current = false;

    function connect() {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      // Dérive ws(s):// à partir de REACT_APP_API_URL (http(s)://...)
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000/api";
      const wsBase = apiUrl.replace(/^http/, "ws").replace(/\/api\/?$/, "");
      const socket = new WebSocket(`${wsBase}/ws/notifications/?token=${encodeURIComponent(token)}`);

      socket.onopen = () => {
        retryDelay.current = 1000; // reset du backoff une fois connecté
      };

      socket.onmessage = (event) => {
        try {
          const notification = JSON.parse(event.data);
          onMessageRef.current?.(notification);
        } catch {
          // payload inattendu — on ignore plutôt que de crasher l'app
        }
      };

      socket.onclose = () => {
        if (closedByUs.current) return;
        setTimeout(connect, retryDelay.current);
        retryDelay.current = Math.min(retryDelay.current * 2, 30000);
      };

      socket.onerror = () => {
        socket.close(); // déclenche onclose → reconnexion
      };

      wsRef.current = socket;
    }

    connect();

    return () => {
      closedByUs.current = true;
      wsRef.current?.close();
    };
  }, [user]);
}
